<template>
  <div class="verify-form">
    <div class="mb-6 absolute top-8 inset-x-8">
      <Alert type="success" v-if="showSendAlert">
        We’ve sent a verification code to {{ composeTransportRecipients }}
      </Alert>

      <Alert type="success" v-if="showReSendAlert">
        We’ve sent a new verification code to {{ composeTransportRecipients }}
      </Alert>

      <FormError :error="error"></FormError>
    </div>

    <PageTitle v-show="!participants" class="mb-4">
      Verify Your Account
    </PageTitle>

    <p v-if="composeTransportLabels" class="mb-4 text-xs">
      To complete your account set up we’ve sent a verification code to your
      {{ composeTransportLabels }}.
    </p>

    <form @submit.prevent="verifyCode()" v-keyboard-submit>
      <InputCodeField
        class="mb-2"
        label="Enter verification Code"
        v-model="code"
        :validation="v$.code"
        help-text="Enter your 6-digit verification code."
        v-autofocus
        data-testid="auth_code"
      ></InputCodeField>

      <AvailableMethodSelector
        v-if="featureOff?.('USER-527-allow-to-skip-verification')"
        class="mb-2"
        :available="confirmationAvailableTransports"
        @select="resendCode($event)"
      ></AvailableMethodSelector>

      <div v-if="featureOn?.('USER-527-allow-to-skip-verification')">
        <snap-link class="block" @click="resendCode()">
          Send New Code
        </snap-link>

        <snap-link
          class="block"
          @click="skipVerification()"
          v-if="canSkipVerification"
          data-testid="verification_skip"
        >
          Skip Verification At This Time
        </snap-link>
      </div>

      <div class="text-right">
        <snap-button
          @click="declineVerification()"
          button-type="button"
          shape="circular"
          size="lg"
          variant="tertiary"
          class="inline-block mr-2"
          >Cancel
        </snap-button>

        <snap-button
          button-type="submit"
          :disabled="state$.is('SUBMITTING')"
          shape="circular"
          size="lg"
          variant="primary"
          class="inline-block"
          data-testid="confirm"
          >Confirm
        </snap-button>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { Options } from "vue-class-component";
import { mapGetters } from "vuex";

import { TransportEnum, TransportType } from "@/types/graphql";

import { Form, Model, Submit } from "@/core/Form";
import { User } from "@/types/graphql";

import Alert from "@/components/snap-ui/Alert.vue";
import InputField from "@/components/snap-ui/InputField.vue";
import InputCodeField from "@/components/snap-ui/InputCodeField.vue";
import FormError from "@/components/ui/FormError.vue";
import AvailableMethodSelector from "@/components/external/AvailableMethodSelector.vue";
import PageTitle from "@/components/external/PageTitle.vue";

import { userSignupConfirmationResend } from "@/services/user.service";

import { RequiredRule, ValidationCodeRule } from "@/util/validator";
import {
  composeTransportLabels,
  composeTransportRecipients,
} from "@/util/transport";

import { $notify } from "@/services/notification";
import { FeatureToggle } from "@/store/treatments.module";
import * as FullStory from "@/util/fullStory";

@Options({
  props: {
    participants: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    PageTitle,
    Alert,
    AvailableMethodSelector,
    InputField,
    InputCodeField,
    FormError,
  },
  computed: {
    ...mapGetters([
      "consumer",
      "confirmationEmail",
      "confirmationAttempts",
      "confirmationAvailableTransports",
      "confirmationUsedTransports",
      "confirmationOccupation",
      "me",
      "featureOff",
      "featureOn",
      "treatmentConfig",
    ]),
  },
})
export default class VerifyForm extends Form {
  confirmationEmail?: string;
  confirmationAttempts?: number;
  confirmationAvailableTransports?: Array<TransportEnum>;
  confirmationUsedTransports?: Array<TransportType>;
  confirmationOccupation?: string;
  me?: User;
  participants?: boolean;
  consumer?: string;

  featureOn?: FeatureToggle;
  featureOff?: FeatureToggle;
  treatmentConfig?: (name: string) => object;

  canSkipVerification = false;

  @Model({
    required: RequiredRule("Verification code is required."),
    code: ValidationCodeRule("Verification code is invalid."),
  })
  code = "";

  codeSent = false;

  get showSendAlert(): boolean {
    return (
      this.state$.is("NORMAL") &&
      Array.isArray(this.confirmationUsedTransports) &&
      this.confirmationAttempts === 1 &&
      this.error === null
    );
  }

  get showReSendAlert(): boolean {
    return (
      this.state$.is("NORMAL") &&
      Array.isArray(this.confirmationUsedTransports) &&
      (this.confirmationAttempts as number) > 1 &&
      this.error === null
    );
  }

  get TransportEnum() {
    return TransportEnum;
  }

  get composeTransportLabels(): string | undefined {
    return composeTransportLabels(this.confirmationUsedTransports);
  }

  get composeTransportRecipients(): string | undefined {
    return composeTransportRecipients(this.confirmationUsedTransports);
  }

  async mounted() {
    await this.$store.dispatch("useTreatments");

    if (
      this.featureOn?.("USER-527-allow-to-skip-verification") &&
      (this.consumer === "raise" ||
        this.confirmationOccupation === "student_or_participant")
    ) {
      const skipVerificationConfig = this.treatmentConfig?.(
        "USER-527-allow-to-skip-verification"
      ) as { timeout: string };
      const timeout = parseFloat(skipVerificationConfig?.timeout || "0");

      setTimeout(() => {
        this.canSkipVerification = true;
      }, timeout * 1000);
    }
  }

  @Submit()
  async resendCode(transport?: TransportEnum): Promise<void> {
    this.codeSent = false;
    this.code = "";

    this.resetValidation(["code"]);

    const result = await userSignupConfirmationResend(
      this.confirmationEmail as string,
      transport
    );

    await this.$store.dispatch("setConfirmationData", {
      usedTransports: result,
    });

    this.codeSent = true;
  }

  @Submit()
  async verifyCode(): Promise<void> {
    if (this.isFormValid()) {
      await this.$store.dispatch("userSignupConfirm", {
        email: this.confirmationEmail,
        code: this.code,
        consumer: this.consumer,
      });

      FullStory.event("Successful Login", {
        user_email: this.confirmationEmail,
      });

      await this.$store.dispatch("clearConfirmationData");
      await this.$router.push({ name: "home" });

      $notify({
        title: "Your Snap! account has been created.",
        type: "success",
      });
    }
  }

  @Submit()
  async skipVerification(): Promise<void> {
    await this.$store.dispatch("userSignupConfirm", {
      email: this.confirmationEmail,
      code: "",
      skip: true,
      consumer: this.consumer,
    });

    FullStory.event("Skip verification", {
      user_email: this.confirmationEmail,
    });

    FullStory.event("Successful Login", {
      user_email: this.confirmationEmail,
    });

    await this.$store.dispatch("clearConfirmationData");
    await this.$router.push({ name: "home" });

    $notify({
      title: "Your Snap! account has been created.",
      type: "success",
    });
  }

  async declineVerification(): Promise<void> {
    await this.$store.dispatch("clearConfirmationData");
    await this.$router.push({ name: "login" });
  }

  reset(): void {
    this.code = "";
  }
}
</script>
